// @ts-check

const baseUrl = import.meta.env.VITE_SOCKET_URL;

/**
 * Sends a review request and returns its JSON body, with a plain error when
 * the server cannot be reached or refuses the request.
 * @param {string} url
 * @param {RequestInit} init
 * @param {string} failure what failed, for the error message
 * @param {unknown} [valueOn404] returned for a 404 instead of an error
 */
async function requestReviews(url, init, failure, valueOn404) {
  let response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    throw new Error("Could not reach the server to load reviews. Check your connection and try again.", { cause: error });
  }
  if (response.status === 404 && valueOn404 !== undefined) return valueOn404;
  if (!response.ok) {
    throw new Error(`${failure}: ${response.status}`);
  }
  return response.json();
}

/**
 * Creates a canvas document from the given files and requests a review of it.
 * @param {{ sessionId: string, files: Array<{ name: string, content: string }>, journals: string[], additionalContext: string }} payload
 * @param {string | null | undefined} userToken
 * @returns {Promise<{ reviewId: string, documentId: string, status: string }>}
 */
export function createReview(payload, userToken) {
  return requestReviews(new URL("reviews", baseUrl).href, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userToken}`,
    },
    body: JSON.stringify(payload),
  }, "Failed to create review");
}

/**
 * @param {string} reviewId
 * @param {string | null | undefined} userToken
 * @returns {Promise<Review>}
 */
export function getReview(reviewId, userToken) {
  return requestReviews(new URL(`reviews/${reviewId}`, baseUrl).href, {
    headers: {
      Authorization: `Bearer ${userToken}`,
    },
  }, "Failed to fetch review");
}

/**
 * Lists reviews for a session — used to tell which canvas documents also
 * have a review report attached, so the artifacts list can open the right
 * panel for each.
 * @param {string} sessionId
 * @param {string | null | undefined} userToken
 * @returns {Promise<Review[]>}
 */
export function listReviews(sessionId, userToken) {
  const url = new URL("reviews", baseUrl);
  url.searchParams.set("sessionId", sessionId);

  // MinoLLM has no review service yet, so a 404 means "no reviews", not a
  // failure to retry.
  return requestReviews(url.href, {
    headers: {
      Authorization: `Bearer ${userToken}`,
    },
  }, "Failed to list reviews", []);
}
